/**
 * Data hooks for the Couriers feature.
 *
 * The list endpoint plus the verify / availability mutations are fully typed
 * via the generated OpenAPI surface — no `as never` casts are needed here.
 *
 * Hooks intentionally keep their public shape narrow (data + status flags +
 * mutateAsync wrappers) so the page stays presentational and feature tests
 * can stub these hooks with plain objects.
 */
import { useMutation, useQuery, useQueryClient, type UseQueryResult } from '@tanstack/react-query';
import { api } from '@favelafood/shared/api/client';
import type { components } from '@favelafood/shared/api/generated/api';

type CourierResponse = components['schemas']['CourierResponse'];
type AvailabilityStatus = components['schemas']['AvailabilityStatusEnum'];
type CourierPage = components['schemas']['PageResponseCourierResponse'];

export interface CourierListFilters {
  /** Optional availability filter; `undefined` means "all". */
  status?: AvailabilityStatus;
  page: number;
  size: number;
}

/** TanStack Query key factory — keeps cache invalidations consistent. */
export const courierQueryKeys = {
  all: ['couriers'] as const,
  list: (filters: CourierListFilters) => ['couriers', 'list', filters] as const,
};

export function useCouriersList(
  filters: CourierListFilters,
): UseQueryResult<CourierPage, Error> {
  return useQuery<CourierPage, Error>({
    queryKey: courierQueryKeys.list(filters),
    queryFn: async () => {
      const query: Record<string, unknown> = {
        page: filters.page,
        size: filters.size,
      };
      if (filters.status) query.status = filters.status;
      const { data, error } = await api.GET('/api/v1/admin/couriers', {
        params: { query: query as { page?: number; size?: number; status?: AvailabilityStatus } },
      });
      if (error) throw error as Error;
      return data as CourierPage;
    },
  });
}

export interface UseVerifyCourierResult {
  verify: (id: string, verified: boolean) => Promise<CourierResponse>;
  isPending: boolean;
}

export function useVerifyCourier(): UseVerifyCourierResult {
  const queryClient = useQueryClient();
  const mutation = useMutation<CourierResponse, Error, { id: string; verified: boolean }>({
    mutationFn: async ({ id, verified }) => {
      const { data, error } = await api.PATCH('/api/v1/admin/couriers/{id}/verify', {
        params: { path: { id } },
        body: { verified },
      });
      if (error) throw error as Error;
      return data as CourierResponse;
    },
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: courierQueryKeys.all });
    },
  });

  return {
    verify: (id, verified) => mutation.mutateAsync({ id, verified }),
    isPending: mutation.isPending,
  };
}

export interface UseToggleAvailabilityResult {
  toggle: (id: string) => Promise<CourierResponse>;
  isPending: boolean;
}

export function useToggleCourierAvailability(): UseToggleAvailabilityResult {
  const queryClient = useQueryClient();
  const mutation = useMutation<CourierResponse, Error, { id: string }>({
    mutationFn: async ({ id }) => {
      const { data, error } = await api.PATCH('/api/v1/admin/couriers/{id}/availability', {
        params: { path: { id } },
      });
      if (error) throw error as Error;
      return data as CourierResponse;
    },
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: courierQueryKeys.all });
    },
  });

  return {
    toggle: (id) => mutation.mutateAsync({ id }),
    isPending: mutation.isPending,
  };
}
